import React, { useRef, useEffect } from 'react';
import createGlobe from 'cobe';

function buildIcosphere() {
  const PHI = (1 + Math.sqrt(5)) / 2;
  const verts = [
    [-1,PHI,0],[1,PHI,0],[-1,-PHI,0],[1,-PHI,0],
    [0,-1,PHI],[0,1,PHI],[0,-1,-PHI],[0,1,-PHI],
    [PHI,0,-1],[PHI,0,1],[-PHI,0,-1],[-PHI,0,1],
  ].map(v => {
    const l = Math.sqrt(v[0]*v[0]+v[1]*v[1]+v[2]*v[2]);
    return [v[0]/l, v[1]/l, v[2]/l];
  });
  const faces = [
    [0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],
    [1,5,9],[5,11,4],[11,10,2],[10,7,6],[7,1,8],
    [3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],
    [4,9,5],[2,4,11],[6,2,10],[8,6,7],[9,8,1],
  ];
  const mc = {}, nf = [];
  const gm = (a, b) => {
    const k = Math.min(a,b)+'_'+Math.max(a,b);
    if (mc[k] !== undefined) return mc[k];
    const va=verts[a], vb=verts[b];
    const m = [(va[0]+vb[0])/2,(va[1]+vb[1])/2,(va[2]+vb[2])/2];
    const l = Math.sqrt(m[0]*m[0]+m[1]*m[1]+m[2]*m[2]);
    m[0]/=l; m[1]/=l; m[2]/=l;
    mc[k] = verts.length; verts.push(m); return mc[k];
  };
  for (const [a,b,c] of faces) {
    const ab=gm(a,b), bc=gm(b,c), ca=gm(c,a);
    nf.push([a,ab,ca],[ab,b,bc],[ca,bc,c],[ab,bc,ca]);
  }
  // Second subdivision
  const mc2 = {}, nf2 = [];
  const gm2 = (a, b) => {
    const k = Math.min(a,b)+'_'+Math.max(a,b);
    if (mc2[k] !== undefined) return mc2[k];
    const va=verts[a], vb=verts[b];
    const m = [(va[0]+vb[0])/2,(va[1]+vb[1])/2,(va[2]+vb[2])/2];
    const l = Math.sqrt(m[0]*m[0]+m[1]*m[1]+m[2]*m[2]);
    m[0]/=l; m[1]/=l; m[2]/=l;
    mc2[k] = verts.length; verts.push(m); return mc2[k];
  };
  for (const [a,b,c] of nf) {
    const ab=gm2(a,b), bc=gm2(b,c), ca=gm2(c,a);
    nf2.push([a,ab,ca],[ab,b,bc],[ca,bc,c],[ab,bc,ca]);
  }
  const edgeSet = new Set();
  for (const [a,b,c] of nf2)
    [[a,b],[b,c],[c,a]].forEach(([i,j]) => edgeSet.add(Math.min(i,j)+'_'+Math.max(i,j)));
  return { verts, edges: Array.from(edgeSet).map(k => k.split('_').map(Number)) };
}

// Simplified continent coastline outlines [lat, lon]
const CONTINENTS = [
  [[72,-168],[70,-141],[60,-139],[54,-130],[48,-124],[40,-124],[33,-120],[28,-115],[25,-110],[26,-97],[29,-90],[30,-84],[25,-80],[30,-81],[35,-75],[40,-74],[42,-70],[45,-67],[47,-53],[52,-56],[55,-60],[60,-64],[63,-83],[68,-90],[72,-110],[75,-140],[72,-168]],
  [[12,-72],[8,-77],[1,-80],[-5,-81],[-6,-77],[-12,-77],[-18,-70],[-25,-66],[-33,-58],[-42,-64],[-52,-70],[-55,-66],[-47,-58],[-38,-55],[-28,-49],[-22,-41],[-15,-39],[-8,-35],[-2,-42],[2,-52],[8,-63],[12,-72]],
  [[36,-6],[38,0],[43,3],[48,-5],[48,2],[53,8],[56,12],[58,12],[60,5],[64,10],[68,16],[70,28],[66,24],[60,28],[56,22],[50,8],[44,8],[41,14],[38,22],[40,28],[44,34],[40,36],[36,32],[35,14],[36,-6]],
  [[37,-2],[36,10],[30,32],[24,36],[12,44],[4,42],[-2,40],[-10,34],[-20,36],[-26,32],[-34,26],[-34,18],[-22,14],[-8,14],[-4,10],[2,10],[5,-2],[5,-16],[10,-16],[18,-16],[25,-14],[30,-8],[35,-2],[37,-2]],
  [[42,36],[50,52],[56,60],[62,68],[68,72],[72,90],[72,100],[68,120],[64,140],[56,138],[50,132],[44,140],[38,138],[32,122],[28,120],[22,114],[16,108],[8,100],[2,96],[-2,100],[-6,105],[-8,115],[-6,106],[4,102],[10,108],[20,106],[24,102],[28,96],[26,90],[24,80],[26,68],[28,56],[32,48],[36,40],[42,36]],
  [[-12,130],[-16,138],[-24,148],[-32,152],[-38,148],[-38,144],[-34,132],[-26,114],[-20,118],[-14,130],[-12,130]],
  [[60,-44],[66,-36],[72,-22],[78,-20],[82,-20],[83,-28],[80,-50],[76,-64],[70,-54],[64,-48],[60,-44]],
  [[50,-6],[52,-2],[55,-2],[58,-5],[58,-3],[55,0],[51,1],[50,-6]],
  [[31,131],[35,133],[38,139],[42,141],[45,142],[42,143],[38,140],[33,136],[31,131]],
];

const GLOBE_CSS = 580;
const PRIMARY = [248, 56, 0];

const ThreeScene = () => {
  const cobeRef = useRef(null);
  const overlayRef = useRef(null);
  const phiRef = useRef(0);
  const globeRef = useRef(null);

  useEffect(() => {
    const cobeCanvas = cobeRef.current;
    const overlayCanvas = overlayRef.current;
    if (!cobeCanvas || !overlayCanvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pxSize = Math.floor(GLOBE_CSS * dpr);
    cobeCanvas.width = pxSize;
    cobeCanvas.height = pxSize;
    const PAD = 120;
    const overlayCSS = GLOBE_CSS + PAD * 2;
    const overlayPx = Math.floor(overlayCSS * dpr);
    overlayCanvas.width = overlayPx;
    overlayCanvas.height = overlayPx;
    const octx = overlayCanvas.getContext('2d');

    const mesh1 = buildIcosphere();
    const mesh2 = buildIcosphere();

    const makeMeshProps = (mesh) => ({
      edgeProps: mesh.edges.map(() => ({
        brightnessBase: 0.3+Math.random()*0.7,
        flickerSpeed: 1+Math.random()*6,
        flickerPhase: Math.random()*Math.PI*2,
        thickness: 0.3+Math.random()*0.6,
      })),
      vertProps: mesh.verts.map(() => {
        const ax=Math.random()-0.5, ay=Math.random()-0.5, az=Math.random()-0.5;
        const al=Math.sqrt(ax*ax+ay*ay+az*az);
        return {
          size:0.3+Math.random()*1, brightness:0.3+Math.random()*0.7,
          flickerSpeed:2+Math.random()*8, flickerPhase:Math.random()*Math.PI*2,
          hasStar:Math.random()<0.2,
          bobSpeed:0.3+Math.random()*0.5, bobPhase:Math.random()*Math.PI*2,
          bobAmount:0.015+Math.random()*0.03,
          driftSpeedA:0.15+Math.random()*0.3, driftSpeedB:0.1+Math.random()*0.25,
          driftPhaseA:Math.random()*Math.PI*2, driftPhaseB:Math.random()*Math.PI*2,
          driftAmount:0.03+Math.random()*0.06,
        };
      }),
    });

    const props1 = makeMeshProps(mesh1);
    const props2 = makeMeshProps(mesh2);

    let jitterX = 0, jitterY = 0, nextJitter = 0;
    // Support multiple simultaneous glitch strikes
    let glitches = [];
    let nextGlitchTime = 1;

    // Mouse/touch interaction
    let mouseX = -1000, mouseY = -1000, mouseActive = false;
    const containerEl = overlayCanvas.parentElement;

    const handlePointerMove = (e) => {
      const rect = containerEl.getBoundingClientRect();
      mouseX = (e.clientX - rect.left) * (overlayCSS / rect.width);
      mouseY = (e.clientY - rect.top) * (overlayCSS / rect.height);
      mouseActive = true;
    };
    const handlePointerLeave = () => { mouseActive = false; mouseX = -1000; mouseY = -1000; };
    const handleTouchMove = (e) => {
      const t = e.touches[0];
      const rect = containerEl.getBoundingClientRect();
      mouseX = (t.clientX - rect.left) * (overlayCSS / rect.width);
      mouseY = (t.clientY - rect.top) * (overlayCSS / rect.height);
      mouseActive = true;
    };

    containerEl.addEventListener('pointermove', handlePointerMove);
    containerEl.addEventListener('pointerleave', handlePointerLeave);
    containerEl.addEventListener('touchmove', handleTouchMove, { passive: true });
    containerEl.addEventListener('touchend', handlePointerLeave);

    const getAnimatedVert = (v, vp, scale, elapsed) => {
      let x=v[0]*scale, y=v[1]*scale, z=v[2]*scale;
      const nx=v[0], ny=v[1], nz=v[2];
      const refX=Math.abs(ny)<0.9?0:1, refY=Math.abs(ny)<0.9?1:0;
      let t1x=-nz*refY, t1y=nz*refX, t1z=nx*refY-ny*refX;
      const t1l=Math.sqrt(t1x*t1x+t1y*t1y+t1z*t1z);
      t1x/=t1l; t1y/=t1l; t1z/=t1l;
      let t2x=ny*t1z-nz*t1y, t2y=nz*t1x-nx*t1z, t2z=nx*t1y-ny*t1x;
      const dA=Math.sin(elapsed*vp.driftSpeedA+vp.driftPhaseA)*vp.driftAmount*scale;
      const dB=Math.sin(elapsed*vp.driftSpeedB+vp.driftPhaseB)*vp.driftAmount*scale;
      x+=t1x*dA+t2x*dB; y+=t1y*dA+t2y*dB; z+=t1z*dA+t2z*dB;
      const bob=Math.sin(elapsed*vp.bobSpeed+vp.bobPhase)*vp.bobAmount;
      x+=nx*bob; y+=ny*bob; z+=nz*bob;
      return [x, y, z];
    };

    const projectVert3D = (xyz, curPhi, offsetX, offsetY, meshOrbitSpeed, elapsed) => {
      let [x,y,z] = xyz;
      const mp=elapsed*meshOrbitSpeed;
      const cm=Math.cos(mp), sm=Math.sin(mp);
      const mx=x*cm+z*sm, mz=-x*sm+z*cm;
      const cp=Math.cos(curPhi), sp=Math.sin(curPhi);
      const x1=mx*cp+mz*sp, z1=-mx*sp+mz*cp;
      const th=0.25, ct=Math.cos(th), st=Math.sin(th);
      const y2=y*ct-z1*st, z2=y*st+z1*ct;
      const h=GLOBE_CSS/2, s=0.8;
      return { x: PAD+h+x1*h*s+offsetX, y: PAD+h-y2*h*s+offsetY, z: z2 };
    };

    // --- Create cobe globe (same config as working Myrro site) ---
    const globe = createGlobe(cobeCanvas, {
      devicePixelRatio: dpr,
      width: pxSize,
      height: pxSize,
      phi: phiRef.current,
      theta: 0.25,
      dark: 1,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.05, 0.05, 0.08],
      markerColor: [0.36, 0.38, 1],
      glowColor: [0.15, 0.15, 0.25],
      markers: [],
    });
    globeRef.current = globe;

    // --- Single animation loop (like Myrro) ---
    let lastTime = performance.now();
    let rafId;

    const animate = (now) => {
      rafId = requestAnimationFrame(animate);
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      const elapsed = now / 1000;

      // Rotate globe
      phiRef.current += 0.25 * dt;
      globe.update({ phi: phiRef.current });

      const curPhi = phiRef.current;
      const globeR = GLOBE_CSS * 0.4;
      // Globe center in overlay coordinates (offset by padding)
      const cx = GLOBE_CSS/2 + PAD, cy = GLOBE_CSS/2 + PAD;

      jitterX = 0; jitterY = 0;

      // Clear overlay
      octx.setTransform(1,0,0,1,0,0);
      octx.clearRect(0, 0, overlayPx, overlayPx);
      octx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // --- White globe outline ---
      octx.beginPath();
      octx.arc(cx, cy, globeR, 0, Math.PI*2);
      octx.strokeStyle = 'rgba(255,255,255,0.15)';
      octx.lineWidth = 1;
      octx.stroke();


      // --- Spawn new glitches ---
      if (elapsed > nextGlitchTime) {
        nextGlitchTime = elapsed + 1.5 + Math.random()*2.5;
        const angle = Math.random() * Math.PI * 2;
        const ex = cx + Math.cos(angle) * globeR;
        const ey = cy + Math.sin(angle) * globeR;
        const hex = () => Math.floor(Math.random()*0xFFFF).toString(16).toUpperCase().padStart(4,'0');
        const coord = () => (Math.random()*180-90).toFixed(4);
        const ip = () => `${10+~~(Math.random()*240)}.${~~(Math.random()*256)}.${~~(Math.random()*256)}.${1+~~(Math.random()*254)}`;

        const blocks = [];
        const count = 6 + Math.floor(Math.random()*10);
        for (let i = 0; i < count; i++) {
          const sa = angle + (Math.random()-0.5)*1.2;
          const dist = Math.random() * globeR * 1.0;
          const bx = cx+Math.cos(sa)*(globeR+dist*1.3);
          const by = cy+Math.sin(sa)*(globeR+dist*1.3);
          const r = Math.random();
          const color = r<0.5 ? `${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]}` : r<0.75 ? '255,140,40' : '255,200,80';
          const fade = 1-(dist/globeR)*0.6;
          blocks.push({ x:bx-Math.random()*45, y:by-Math.random()*5, w:8+Math.random()*90, h:1+Math.random()*10, color, alpha:(0.15+Math.random()*0.35)*fade, ox:(Math.random()-0.5)*5 });
        }

        glitches.push({
          angle, edgeX: ex, edgeY: ey,
          framesLeft: 40 + Math.floor(Math.random()*60),
          totalFrames: 40 + Math.floor(Math.random()*60),
          blocks,
          data: [
            `0x${hex()}${hex()}`,
            `LAT ${coord()} LON ${coord()}`,
            `NODE ${ip()}`,
            `SIG ${(Math.random()*100).toFixed(1)}dB`,
            `ERR 0x${hex()}`,
          ],
        });
        // Keep max 3 simultaneous
        if (glitches.length > 3) glitches.shift();
      }

      // --- Draw mesh (affected by all active glitches) ---
      const anyGlitchActive = glitches.some(g => g.framesLeft > 0);

      const drawMesh = (meshData, meshProps, scale, orbitSpeed, color, backAlphaMult, radialBob) => {
        const { verts: mv, edges: me } = meshData;
        const { edgeProps: mep, vertProps: mvp } = meshProps;
        const c = color.join(',');

        const proj = mv.map((v, vi) => {
          const vp = mvp[vi];
          const anim = getAnimatedVert(v, vp, scale, elapsed);
          if (radialBob) {
            const nx=v[0], ny=v[1], nz=v[2];
            const extraBob = Math.sin(elapsed*vp.bobSpeed*0.7 + vp.bobPhase) * 0.08
                           + Math.sin(elapsed*vp.driftSpeedA*1.3 + vp.driftPhaseA) * 0.05;
            anim[0] += nx * extraBob;
            anim[1] += ny * extraBob;
            anim[2] += nz * extraBob;
          }
          const p = projectVert3D(anim, curPhi, jitterX, jitterY, orbitSpeed, elapsed);
          // Distort from all active glitches
          for (const g of glitches) {
            if (g.framesLeft <= 0) continue;
            const dx=p.x-g.edgeX, dy=p.y-g.edgeY;
            const dist=Math.sqrt(dx*dx+dy*dy);
            if (dist < globeR*0.8 && dist > 0) {
              const intensity = Math.pow(1-dist/(globeR*0.8), 1.3);
              const push = intensity * 30;
              p.x += (dx/dist)*push*(0.5+Math.random()*0.5);
              p.y += (dy/dist)*push*(0.5+Math.random()*0.5);
              p.x += (Math.random()-0.5) * intensity * 10;
              p.y += (Math.random()-0.5) * intensity * 10;
            }
          }
          // Mouse/touch distortion — attract toward cursor with jitter
          if (mouseActive) {
            const dx=p.x-mouseX, dy=p.y-mouseY;
            const dist=Math.sqrt(dx*dx+dy*dy);
            const radius = globeR * 0.7;
            if (dist < radius && dist > 0) {
              const intensity = Math.pow(1-dist/radius, 1.3);
              // Pull toward cursor (negative = attract)
              const pull = intensity * 20;
              p.x -= (dx/dist)*pull*(0.6+Math.random()*0.4);
              p.y -= (dy/dist)*pull*(0.6+Math.random()*0.4);
              // Lateral jitter like glitch
              p.x += (Math.random()-0.5) * intensity * 15;
              p.y += (Math.random()-0.5) * intensity * 15;
            }
          }
          return p;
        });

        octx.save();
        octx.beginPath();
        octx.arc(cx, cy, globeR * 1.25, 0, Math.PI * 2);
        octx.clip();

        for (let ei = 0; ei < me.length; ei++) {
          const [ai,bi] = me[ei];
          const ep = mep[ei];
          const pa = proj[ai], pb = proj[bi];
          if (pa.z < -0.1 || pb.z < -0.1) continue;
          const flicker = 0.5+0.5*Math.sin(elapsed*ep.flickerSpeed+ep.flickerPhase);
          const alpha = 0.3*ep.brightnessBase*(0.4+flicker*0.6);
          if (Math.random() < 0.005) continue;

          // Check if near any active glitch
          let ec = c;
          const mx=(pa.x+pb.x)/2, my=(pa.y+pb.y)/2;
          if (anyGlitchActive) {
            for (const g of glitches) {
              if (g.framesLeft <= 0) continue;
              if (Math.sqrt((mx-g.edgeX)**2+(my-g.edgeY)**2) < globeR*0.5) {
                ec = `${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]}`;
                break;
              }
            }
          }
          // Mouse hover — orange highlight
          if (mouseActive && ec === c) {
            if (Math.sqrt((mx-mouseX)**2+(my-mouseY)**2) < globeR*0.5) {
              ec = `${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]}`;
            }
          }
          octx.beginPath(); octx.moveTo(pa.x, pa.y); octx.lineTo(pb.x, pb.y);
          octx.strokeStyle = `rgba(${ec},${Math.max(0.01, alpha)})`;
          octx.lineWidth = ep.thickness; octx.stroke();
        }

        for (let i = 0; i < mv.length; i++) {
          const vp = mvp[i]; const p = proj[i]; if (p.z < 0) continue;
          const flicker = 0.5+0.5*Math.sin(elapsed*vp.flickerSpeed+vp.flickerPhase);
          const alpha = 0.5*vp.brightness*(0.5+flicker*0.5);
          const ns = (vp.size+1.5)*(0.7+flicker*0.3);
          if (Math.random() < 0.01) continue;
          let nc = c, nsc = 1;
          if (anyGlitchActive) {
            for (const g of glitches) {
              if (g.framesLeft <= 0) continue;
              const d = Math.sqrt((p.x-g.edgeX)**2+(p.y-g.edgeY)**2);
              if (d < globeR*0.5) { nc=`${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]}`; nsc=1.5; break; }
            }
          }
          // Mouse hover — orange highlight + enlarge
          if (mouseActive && nc === c) {
            const d = Math.sqrt((p.x-mouseX)**2+(p.y-mouseY)**2);
            if (d < globeR*0.5) { nc=`${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]}`; nsc=1.4; }
          }
          octx.beginPath(); octx.arc(p.x, p.y, ns*nsc, 0, Math.PI*2);
          octx.fillStyle = `rgba(${nc},${alpha})`; octx.fill();
        }
        octx.restore();
      };

      drawMesh(mesh1, props1, 1.12, 0.12, [255,255,255], 1.0, true);
      drawMesh(mesh2, props2, 1.04, 0.08, [200,210,220], 0.6, false);

      // --- Render each glitch ---
      for (const g of glitches) {
        if (g.framesLeft <= 0) continue;
        g.framesLeft--;

        // Streaks — radiate outward from strike point in all directions
        const sc = 3+Math.floor(Math.random()*5);
        for (let s = 0; s < sc; s++) {
          const sa = g.angle+(Math.random()-0.5)*1.2;
          const len = 15+Math.random()*globeR*0.6;
          octx.beginPath(); octx.moveTo(g.edgeX, g.edgeY);
          octx.lineTo(g.edgeX+Math.cos(sa)*len, g.edgeY+Math.sin(sa)*len);
          octx.strokeStyle = `rgba(${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]},${0.1+Math.random()*0.2})`;
          octx.lineWidth = 1+Math.random()*2; octx.stroke();
        }

        // Glow
        const pa = 0.04+0.03*Math.sin(elapsed*3);
        const glow = octx.createRadialGradient(g.edgeX, g.edgeY, 0, g.edgeX, g.edgeY, globeR*0.4);
        glow.addColorStop(0, `rgba(${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]},${pa})`);
        glow.addColorStop(0.4, `rgba(${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]},${pa*0.3})`);
        glow.addColorStop(1, 'rgba(0,0,0,0)');
        octx.fillStyle = glow;
        octx.beginPath(); octx.arc(g.edgeX, g.edgeY, globeR*0.4, 0, Math.PI*2); octx.fill();

        // Data readout text
        const fadeIn = Math.min(1, (g.totalFrames - g.framesLeft) / 10);
        const fadeOut = Math.min(1, g.framesLeft / 8);
        const textAlpha = Math.min(fadeIn, fadeOut);
        const awayX = Math.cos(g.angle);
        const awayY = Math.sin(g.angle);
        const textX = g.edgeX + awayX * 35;
        const textY = g.edgeY + awayY * 35;

        octx.save();
        octx.font = '9px monospace';
        octx.textBaseline = 'top';
        for (let i = 0; i < g.data.length; i++) {
          const lineY = textY + i * 12;
          const glitchOff = Math.random() < 0.15 ? (Math.random()-0.5)*4 : 0;
          octx.fillStyle = `rgba(${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]},${textAlpha * 0.15})`;
          octx.fillText(g.data[i], textX + glitchOff + 1, lineY + 1);
          octx.fillStyle = `rgba(${PRIMARY[0]},${PRIMARY[1]},${PRIMARY[2]},${textAlpha * 0.7})`;
          octx.fillText(g.data[i], textX + glitchOff, lineY);
        }
        octx.restore();
      }

      // Clean up expired glitches
      while (glitches.length > 0 && glitches[0].framesLeft <= 0) glitches.shift();
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      globe.destroy();
      containerEl.removeEventListener('pointermove', handlePointerMove);
      containerEl.removeEventListener('pointerleave', handlePointerLeave);
      containerEl.removeEventListener('touchmove', handleTouchMove);
      containerEl.removeEventListener('touchend', handlePointerLeave);
    };
  }, []);

  // Globe container scales responsively
  // PAD as percentage of globe size
  const padPct = (120 / GLOBE_CSS) * 100;
  const totalPct = 100 + padPct * 2;
  const globePct = (GLOBE_CSS / (GLOBE_CSS + 240)) * 100;
  const padOffsetPct = (120 / (GLOBE_CSS + 240)) * 100;

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: GLOBE_CSS + 240,
      aspectRatio: '1',
      margin: '-40px auto -40px',
    }}>
      <canvas ref={cobeRef} style={{
        position: 'absolute',
        top: `${padOffsetPct}%`,
        left: `${padOffsetPct}%`,
        width: `${globePct}%`,
        height: `${globePct}%`,
        display: 'block',
      }} />
      <canvas ref={overlayRef} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 2,
      }} />
    </div>
  );
};

export default ThreeScene;
